import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { Spinner, ErrorBanner } from '../ReportTableKit';

const API = process.env.REACT_APP_BACKEND_URL;
const A_COLOR = '#194449';
const B_COLOR = '#f59e0b';
const ROWS = [
  ['enrollments', 'Total Enrollments'], ['completions', 'Total Completions'], ['vouchers', 'Vouchers Issued'],
  ['completion_rate', 'Completion Rate', '%'], ['vouchers_vs_completions', 'Vouchers vs Completions', '%'],
  ['cert_courses', 'Cert Courses (incl. Languages)'], ['unique_cert_courses', 'Unique Cert Courses'],
];
const METRICS = [['enrollments', 'Enrollments'], ['completions', 'Completions'], ['vouchers', 'Vouchers']];
const fmt = (v, pct) => (pct ? `${v ?? 0}%` : (v ?? 0).toLocaleString());

function Delta({ a, b, pct }) {
  if (!a && !b) return <span style={{ color: '#94a3b8' }}>—</span>;
  const diff = pct ? +(b - a).toFixed(1) : (a ? +(((b - a) / a) * 100).toFixed(1) : null);
  if (diff === null) return <span style={{ color: '#94a3b8' }}>new</span>;
  const up = diff >= 0;
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span style={{ color: up ? '#15803d' : '#b91c1c', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <Icon size={13} />{Math.abs(diff)}{pct ? ' pts' : '%'}
    </span>
  );
}

function CompareTable({ a, b }) {
  return (
    <div className="table-container" data-testid="compare-table" style={{ marginBottom: 20 }}>
      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th style={{ textAlign: 'right', color: A_COLOR }}>{a.campaign}<div style={{ fontWeight: 400, fontSize: 11, color: '#64748b' }}>{a.date_from} → {a.date_to} · {a.days} days</div></th>
            <th style={{ textAlign: 'right', color: B_COLOR }}>{b.campaign}<div style={{ fontWeight: 400, fontSize: 11, color: '#64748b' }}>{b.date_from} → {b.date_to} · {b.days} days</div></th>
            <th style={{ textAlign: 'right' }}>B vs A</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map(([k, l, pct]) => (
            <tr key={k} data-testid={`compare-row-${k}`}>
              <td style={{ fontWeight: 600 }}>{l}</td>
              <td style={{ textAlign: 'right' }} data-testid={`compare-a-${k}`}>{fmt(a.cards[k], pct)}</td>
              <td style={{ textAlign: 'right' }} data-testid={`compare-b-${k}`}>{fmt(b.cards[k], pct)}</td>
              <td style={{ textAlign: 'right' }}><Delta a={a.cards[k]} b={b.cards[k]} pct={pct} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DayChart({ a, b }) {
  const [metric, setMetric] = useState('enrollments');
  const [cumulative, setCumulative] = useState(true);
  const data = useMemo(() => {
    const len = Math.max(a.daily.length, b.daily.length);
    let sa = 0; let sb = 0;
    return Array.from({ length: len }, (_, i) => {
      const va = a.daily[i]?.[metric]; const vb = b.daily[i]?.[metric];
      sa += va || 0; sb += vb || 0;
      return { day: i + 1, A: va === undefined ? null : (cumulative ? sa : va), B: vb === undefined ? null : (cumulative ? sb : vb) };
    });
  }, [a, b, metric, cumulative]);

  return (
    <div className="stat-card" data-testid="compare-chart" style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginRight: 'auto' }}>Days since campaign start</div>
        {METRICS.map(([k, l]) => (
          <button key={k} onClick={() => setMetric(k)} data-testid={`compare-metric-${k}`}
            className={metric === k ? 'btn btn-primary' : 'btn btn-secondary'} style={{ padding: '4px 12px', fontSize: 12 }}>{l}</button>
        ))}
        <label style={{ fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
          <input type="checkbox" checked={cumulative} onChange={e => setCumulative(e.target.checked)} data-testid="compare-cumulative" /> Cumulative
        </label>
      </div>
      <div style={{ height: 340 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} label={{ value: 'Day', position: 'insideBottomRight', offset: -4, fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip labelFormatter={d => `Day ${d}`} formatter={v => Number(v).toLocaleString()} />
            <Legend wrapperStyle={{ fontSize: 12 }} itemSorter={null} />
            <Line type="linear" dataKey="A" name={a.campaign} stroke={A_COLOR} strokeWidth={2} dot={false} connectNulls={false} />
            <Line type="linear" dataKey="B" name={b.campaign} stroke={B_COLOR} strokeWidth={2} dot={false} connectNulls={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function CampaignCompare({ campaignA, campaignB }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!campaignA || !campaignB) return;
    let live = true;
    setData(null); setError(null);
    axios.get(`${API}/api/cert-prep/v2/compare`, { params: { campaign_a: campaignA, campaign_b: campaignB }, withCredentials: true })
      .then(r => { if (live) setData(r.data); })
      .catch(e => { if (live) setError(e.response?.data?.detail || 'Failed to load comparison.'); });
    return () => { live = false; };
  }, [campaignA, campaignB]);

  if (campaignA === campaignB) return <ErrorBanner message="Pick two different campaigns to compare." testId="compare-same-error" />;
  if (error) return <ErrorBanner message={error} testId="compare-error" />;
  if (!data) return <div className="table-container"><Spinner /></div>;
  return (
    <div data-testid="campaign-compare">
      <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 10px' }}>Each campaign uses its full run: first voucher date to last activity (vouchers, enrollments or completions in its eligible courses, up to today).</p>
      <CompareTable a={data.a} b={data.b} />
      <DayChart a={data.a} b={data.b} />
    </div>
  );
}
